import type { InputBaseModel, NodeBaseModel } from '../models';
import { InstanceUtils } from '../misc';
import type { ContractChangeReferenceDto } from './types';

export function contractInputReference(input: InputBaseModel): ContractChangeReferenceDto {
    const node = input.ownerNode;
    let layerId: string | undefined;
    let sectionId: string | undefined;
    if (InstanceUtils.isLayersNode(node)) {
        layerId = node.getLayerByInputGuid(input.guid)?.id;
    }
    if (InstanceUtils.isSectionsNode(node)) {
        sectionId = node.getSectionByInputGuid(input.guid)?.id;
    }
    if (InstanceUtils.isLayersWithSectionsNode(node)) {
        const layer = node.getLayerByInputGuid(input.guid);
        layerId = layer?.id;
        sectionId = layer?.getSectionByInputGuid(input.guid)?.id;
    }
    return referenceInScope(input, layerId, sectionId, id => input.inputsAtSameLevel.find(i => i.id === id)?.guid);
}

function referenceInScope(
    input: InputBaseModel,
    layerId: string | undefined,
    sectionId: string | undefined,
    typeGuid: (id: string) => string | undefined
): ContractChangeReferenceDto {
    const match = /^(Limit|Deduction)(Type|Value)_(\d+)$/.exec(input.id);
    return {
        nodeId: input.ownerNode.id,
        itemId: match ? `${match[1]}${match[2]}` : input.id,
        ...(layerId ? { layerId } : {}),
        ...(sectionId ? { sectionId } : {}),
        ...(match ? { recordId: typeGuid(`${match[1]}Type_${match[3]}`) || `${match[1]}_${match[3]}` } : {})
    };
}

// Bulk comparisons already know each scope. Index repeated type IDs once instead
// of searching the node and scope arrays again for every field.
export function contractInputReferences(nodes: NodeBaseModel[]) {
    return contractInputGroups(nodes).flatMap(group => contractInputGroupEntries(group));
}

export function contractInputGroupEntries(
    group: { layerId?: string; sectionId?: string; inputs: InputBaseModel[] },
    inputs = group.inputs
) {
    const typeGuids = new Map<string, string>();
    inputs.forEach(input => {
        if (!typeGuids.has(input.id)) {
            typeGuids.set(input.id, input.guid);
        }
    });
    return inputs.map(input => {
        const reference = referenceInScope(input, group.layerId, group.sectionId, id => typeGuids.get(id));
        return { input, reference, key: contractReferenceKey(reference) };
    });
}

export function draftScopeKey(nodeId: string, layerId?: string | null, sectionId?: string | null) {
    return JSON.stringify([nodeId, layerId ?? null, sectionId ?? null]);
}

export function contractInputGroups(nodes: NodeBaseModel[]) {
    const groups: {
        id: string;
        node: NodeBaseModel;
        layerId?: string;
        sectionId?: string;
        inputs: InputBaseModel[];
        replace: (inputs: InputBaseModel[]) => void;
    }[] = [];
    const visit = (node: NodeBaseModel) => {
        const add = (owner: { inputs: InputBaseModel[] }, layerId?: string, sectionId?: string) =>
            groups.push({
                id: draftScopeKey(node.id, layerId, sectionId),
                node,
                layerId,
                sectionId,
                inputs: owner.inputs,
                replace: inputs => {
                    owner.inputs = inputs;
                }
            });
        if (InstanceUtils.isBasicNode(node)) {
            add(node);
        }
        if (InstanceUtils.isLayersNode(node)) {
            node.layers.forEach(layer => add(layer, layer.id));
        }
        if (InstanceUtils.isSectionsNode(node)) {
            node.sections.forEach(section => add(section, undefined, section.id));
        }
        if (InstanceUtils.isLayersWithSectionsNode(node)) {
            node.layers.forEach(layer => layer.sections.forEach(section => add(section, layer.id, section.id)));
        }
        node.childNodes.forEach(visit);
    };
    nodes.forEach(visit);
    return groups;
}

export function contractReferenceKey(reference: ContractChangeReferenceDto) {
    return JSON.stringify([
        reference.nodeId ?? null,
        reference.itemId,
        reference.layerId ?? null,
        reference.sectionId ?? null,
        reference.recordId ?? null
    ]);
}

// Repeated form IDs are translated at the form/history boundary only.
export function contractRecordReference(reference: ContractChangeReferenceDto): ContractChangeReferenceDto | undefined {
    const record = /^(Limit|Deduction)(?:Type|Value)?$/.exec(reference.itemId);
    return reference.recordId && record ? { ...reference, itemId: record[1] } : undefined;
}
